import { Box, Typography } from '@mui/material';
import UPButton from 'components/button';
import { UPMultipleSelectionDropdown } from 'components/dropdown';
import ErrorBar from 'components/error-bar';
import UPSwitch from 'components/switch';
import { LinkType } from 'constants/LinkType';
import { toNumber } from 'lodash';
import { SectionContainer } from 'modules/companies/styles';
import { useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store';
import { addCompanyEmployeeInvitationLink } from 'store/actions/employee-actions';
import { addJobApplicantLink } from 'store/actions/job-applicant-link-actions';
import {
  employeeCompanyEmployeeInvitationLinkErrorSelector,
  employeeCompanyEmployeeInvitationLinkSuccessSelector,
} from 'store/selectors/employee-selector';
import {
  jobApplicantLinkErrorSelector,
  jobApplicantLinkSuccessSelector,
  jobApplicantLinksLoadingSelector,
} from 'store/selectors/job-applicant-link-selectors';
import {
  EMPLOYEES__APPLICATION_LINK_SAVED,
  EMPLOYEES__INVITE_EMPLOYEE_SAVED,
  EMPLOYEES__IS_FOR_ALL_LOCATIONS,
  EMPLOYEE__COMPANY_LOCATION,
  GENERAL__CANCEL_BUTTON,
  GENERAL__CREATE,
} from 'translations/constants';
import ICompanyInvitationLink from 'types/employee/ICompanyInvitationLink';
import { getInputErrorText } from 'utils/helpers';
import { AlertStyled } from '../../styles';
import { ModalFooter, RowContainerForContactInfo } from '../styles';
import { IAddGeneralInvitationProps } from '../types';
import LinkTypeSelector from './LinkTypeSelector';

interface ICreateLinkTabProps extends IAddGeneralInvitationProps {
  /** When false the Link type selector is hidden and this behaves exactly as before. */
  jobApplicantsEnabled: boolean;
}

const CreateLinkTab = (props: ICreateLinkTabProps) => {
  const { companyId, locations, onClose, jobApplicantsEnabled } = props;
  const [t] = useTranslation();
  const dispatch = useAppDispatch();

  const [isForAllLocations, setIsForAllLocations] = useState<boolean>(false);
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const {
    handleSubmit,
    control,
    getValues,
    watch,
    formState: { errors },
  } = useForm<any>({
    defaultValues: { linkType: LinkType.Onboarding },
  });

  const linkType = watch('linkType') ?? LinkType.Onboarding;
  const isApplicationLink = jobApplicantsEnabled && linkType === LinkType.Application;

  const invitationError = useAppSelector(employeeCompanyEmployeeInvitationLinkErrorSelector);
  const invitationSuccess = useAppSelector(employeeCompanyEmployeeInvitationLinkSuccessSelector);
  const applicationError = useAppSelector(jobApplicantLinkErrorSelector);
  const applicationSuccess = useAppSelector(jobApplicantLinkSuccessSelector);
  const applicationLoading = useAppSelector(jobApplicantLinksLoadingSelector);

  const error = isApplicationLink ? applicationError : invitationError;
  const success = isApplicationLink ? applicationSuccess : invitationSuccess;
  const successText = isApplicationLink ? t(EMPLOYEES__APPLICATION_LINK_SAVED) : t(EMPLOYEES__INVITE_EMPLOYEE_SAVED);

  // Drive the auto-hide off showMessage. Keying the timer on mount (with an empty dep array)
  // meant the bar that onSave raises afterwards never cleared.
  useEffect(() => {
    if (!showMessage) return undefined;
    const timeId = setTimeout(() => setShowMessage(false), 3000);
    return () => clearTimeout(timeId);
  }, [showMessage]);

  const onSave: SubmitHandler<any> = async () => {
    const values = getValues();
    const selectedLocationIds: number[] = values.companyLocationIds?.toString().split(',').map(Number) ?? [];
    const allLocationIds: number[] = locations.map(location => toNumber(location.value));

    setIsSaving(true);
    try {
      if (isApplicationLink) {
        // The JobApplicants API requires at least one explicit branch id and has no
        // "all branches" flag — "select all" is expanded here, deliberately, so the link's
        // reach never changes when the company gains a branch later.
        await dispatch(
          addJobApplicantLink({
            companyId: toNumber(companyId),
            locationIds: values.isForAllLocations ? allLocationIds : selectedLocationIds,
          }),
        );
      } else {
        const objectToSend: ICompanyInvitationLink = {
          id: 0,
          companyId: toNumber(companyId),
          locationIds: values.isForAllLocations ? [] : selectedLocationIds,
          isForAllLocations: values.isForAllLocations,
        };
        await dispatch(addCompanyEmployeeInvitationLink(objectToSend));
      }
    } finally {
      setIsSaving(false);
      setShowMessage(true);
    }
  };

  const isSubmitDisabled = isSaving || (isApplicationLink && applicationLoading);

  return (
    <SectionContainer>
      {showMessage && error && (
        <Box>
          <ErrorBar message={t(error)} />
        </Box>
      )}

      {showMessage && success && <AlertStyled severity="success">{successText}</AlertStyled>}

      {jobApplicantsEnabled && (
        <RowContainerForContactInfo>
          <Box sx={{ display: 'inline-block', width: '70%' }}>
            <Controller
              name="linkType"
              control={control}
              render={({ field }) => (
                <LinkTypeSelector value={field.value ?? LinkType.Onboarding} onChange={field.onChange} />
              )}
            />
          </Box>
        </RowContainerForContactInfo>
      )}

      <RowContainerForContactInfo>
        <Box sx={{ display: 'inline-block', marginRight: '16px', width: '70%' }}>
          <Controller
            name="companyLocationIds"
            control={control}
            rules={{ required: !isForAllLocations }}
            render={({ field: { onChange, value: currentValue } }) => {
              return (
                <UPMultipleSelectionDropdown
                  items={locations}
                  handleChange={event => {
                    const {
                      target: { value },
                    } = event;
                    onChange(value);
                  }}
                  value={!isForAllLocations ? currentValue ?? [] : []}
                  dropdownLabel={t(EMPLOYEE__COMPANY_LOCATION)}
                  placeholder={locations.length === 0 ? 'No locations available' : t(EMPLOYEE__COMPANY_LOCATION)}
                  disabled={isForAllLocations}
                  helperText={getInputErrorText(errors, 'companyLocationIds')}
                  error={!!errors.companyLocationIds}
                  required
                />
              );
            }}
          />
        </Box>
        <Box>
          <Controller
            name="isForAllLocations"
            control={control}
            render={({ field }) => (
              <Box sx={{ display: 'flex', paddingTop: '1.1rem' }}>
                <UPSwitch
                  checked={field.value}
                  onChange={e => {
                    field.onChange(e);
                    setIsForAllLocations(Boolean(e.target.checked));
                  }}
                />
                <Typography sx={{ ml: '10px' }}>{t(EMPLOYEES__IS_FOR_ALL_LOCATIONS)}</Typography>
              </Box>
            )}
          />
        </Box>
      </RowContainerForContactInfo>
      <ModalFooter>
        <UPButton text={t(GENERAL__CREATE)} onClick={() => handleSubmit(onSave)()} disabled={isSubmitDisabled} />
        <Box sx={{ marginLeft: '12px' }}>
          <UPButton text={t(GENERAL__CANCEL_BUTTON)} onClick={onClose} outlined />
        </Box>
      </ModalFooter>
    </SectionContainer>
  );
};

export default CreateLinkTab;
